import { PageType } from './types';
import { convertUrlToListingPageState } from 'Client/components/ListingPage/services/listingPageUrlService';
import { normalizePath } from 'Common/helpers/urlHelpers';
import paths from 'Common/routes/paths';
import { ensureEndingSlash } from 'Client/urlService';
import { Segments } from 'Common/types/segments';

export interface PageTypeResult {
    pageType: PageType;
    relativeUrl?: string;
}

export const homePagePaths = ['/', '/index', '/index/index'];

export const isHomePage = (pathName: string) => homePagePaths.includes(pathName);

const isPdp = (pathName: string) => pathName.endsWith('.html/');

const aliceRootPaths = [
    '/m/',
    '/customer/',
    '/i/',
    '/helpcenter/',
    '/ajax/',
    '/clubintegration/',
    '/checkout/',
    '/review/',
    '/api/',
    '/cart/',
    '/catalog/',
    '/looks/',
    '/magazine/',
    '/product/',
    '/productlist/',
    '/search/',
    '/smartbanner/',
    '/index/',
    '/sso_login',
    '/sso_pixel/',
    '/sso/',
    '/magazin/',
    '/app/',
    '/ww-shop/',
    '/.well-known/',
];

const aliceExactPaths = ['/brands/'];

const isAlicePage = (pathName: string) => {
    return aliceRootPaths.some(pathPart => pathName.startsWith(pathPart)) || aliceExactPaths.includes(pathName);
};

const isWishListPage = (pathName: string): boolean => {
    return pathName.includes('/customer/wishlist/index/');
};

const isLooks = (pathName: string): boolean => {
    return pathName.includes('/looks/');
};

const isInteriorDesignService = (pathName: string): boolean => {
    return pathName.includes('/m/westwingstudio/') || pathName.includes('/m/interiorservice/');
};

const isAliceStaticPage = (pathName: string): boolean => {
    return pathName.includes('/m/') || pathName.includes('/i/');
};

export const isNativeAppPage = (pathName: string = '', segments: Segments = {}): boolean => {
    const { platform = 'web', mobileAppWebviewVersion = '0.0.0', isWebView } = segments;
    return pathName.includes('/app/') || platform !== 'web' || mobileAppWebviewVersion !== '0.0.0' || !!isWebView;
};

const isCartWebPage = (pathName: string) => pathName === ensureEndingSlash(paths.CART);
const isCartNativePage = (pathName: string) => pathName === ensureEndingSlash(paths.CART_NATIVE);
const isAliceCartPage = (pathName: string) => pathName.startsWith(paths.ALICE_CART);
const isWishlistNativePage = (pathName: string) => pathName.startsWith(paths.WISHLIST_NATIVE);
const isWishlistPage = (pathName: string) => pathName.startsWith(paths.WISHLIST);
const isInteriorDesignServiceNativePage = (pathName: string) =>
    pathName.startsWith(paths.INTERIOR_DESIGN_SERVICE_NATIVE);
const isCheckoutWebPage = (pathName: string) => pathName.startsWith(paths.CHECKOUT);
const isCheckoutNativePage = (pathName: string) => pathName.startsWith(paths.CHECKOUT_NATIVE);
const isAssemblyServiceAppOverlay = (pathName: string) => pathName === ensureEndingSlash(paths.ASSEMBLYSERVICE);

// eslint-disable-next-line complexity
export const getPageType = (pathName: string): PageType => {
    if (isCheckoutWebPage(pathName)) {
        return PageType.CHECKOUT;
    }
    if (isCheckoutNativePage(pathName)) {
        return PageType.CHECKOUT_NATIVE;
    }
    if (isAssemblyServiceAppOverlay(pathName)) {
        return PageType.ASSEMBLYSERVICE;
    }
    if (isWishlistNativePage(pathName)) {
        return PageType.WISHLIST_NATIVE;
    }
    if (isWishlistPage(pathName)) {
        return PageType.WISHLIST;
    }

    if (isInteriorDesignServiceNativePage(pathName)) {
        return PageType.INTERIOR_DESIGN_SERVICE_NATIVE;
    }

    if (isCartWebPage(pathName)) {
        return PageType.CART;
    }

    if (isCartNativePage(pathName)) {
        return PageType.CART_NATIVE;
    }

    if (isAliceCartPage(pathName)) {
        return PageType.ALICE_CART;
    }

    if (isWishListPage(pathName)) {
        return PageType.ALICE_WISHLIST;
    }

    if (isLooks(pathName)) {
        return PageType.LOOKS;
    }

    if (isInteriorDesignService(pathName)) {
        return PageType.INTERIOR_DESIGN_SERVICE;
    }

    if (isAliceStaticPage(pathName)) {
        return PageType.ALICE_STATIC_PAGE;
    }

    if (isAlicePage(pathName)) {
        return PageType.ALICE_OTHER;
    }

    if (isHomePage(pathName)) {
        return PageType.HOMEPAGE;
    }

    if (isPdp(pathName)) {
        return PageType.PDP;
    }

    const { listingPageType } = convertUrlToListingPageState(pathName, '');

    return listingPageType;
};

export const getPageTypeByUrl = (urlString: string, host: string): PageTypeResult => {
    try {
        const { pathname, hostname, protocol, search, port, hash } = new URL(urlString, host);
        const relativeUrl = `${pathname}${search}${hash}`;
        if (`${protocol}//${hostname}${port ? `:${port}` : ''}` !== host) {
            return { pageType: PageType.EXTERNAL, relativeUrl };
        }
        const normalizedPath = normalizePath(pathname);
        return { pageType: getPageType(normalizedPath), relativeUrl };
    } catch (err) {
        return { pageType: PageType.UNKNOWN };
    }
};
